import { forwardRef, type ReactNode, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import type React from "react";
import { useDebouncedCallback, useThrottledCallback } from "use-debounce";

import { cn } from "@/components/podkit/lib/cn";
import { useMountEffect } from "@/hooks/use-mount-effect";

type ScrollAreaProps = {
    id?: string;
    children: ReactNode;
    orientation?: "horizontal" | "vertical" | "both";
    // Note: Don't use this to add padding or layout properties. Apply those styles to the child element instead.
    className?: string;
    // Note: Auto-scroll is currently only supported for the vertical orientation
    autoScroll?: boolean;
    autoScrollContentRef?: React.RefObject<HTMLDivElement>;
    /**
     * Callback function that gets triggered when the user scrolls to the end of the content
     */
    onScrollEnd?: () => void;
    /**
     * Threshold in pixels from the bottom to trigger onScrollEnd (default: 50)
     */
    scrollEndThreshold?: number;
    /**
     * Key used to store and restore scroll position across component mounts.
     *
     * For now, the position is stored in Session Storage, so it persists across page reloads but is cleared when the browser/tab
     * is closed; this means we don't have to manage cleanup of old keys.
     *
     * This is implemented at the component level rather than using React Router's scroll restoration feature
     * as that only supports full page scroll restoration of <body /> [1] which isn't sufficient for us .
     * I also tried epicweb-dev/restore-scroll [2] but that isn't sensitive to scrollable containers where the content
     * isn't instantly loaded.
     *
     * [1]: https://github.com/remix-run/react-router/discussions/9495
     * [2]: https://github.com/epicweb-dev/restore-scroll/tree/main
     */
    scrollRestorationKey?: string;
    /**
     * Callback function that gets triggered when scroll state changes
     * Provides information about whether the user is at the bottom of the scroll area
     */
    onScrollStateChange?: (isAtBottom: boolean) => void;
    /**
     * Callback fired when auto-scroll is enabled or disabled.
     * Consumers can use this to suppress their own scroll adjustments
     * (e.g. virtualizer corrections) while auto-scroll is off.
     */
    onAutoScrollChange?: (enabled: boolean) => void;
};

export type ScrollAreaHandle = {
    scrollToBottom: () => void;
};

export const ScrollArea = forwardRef<ScrollAreaHandle, ScrollAreaProps>(
    (
        {
            id,
            children,
            orientation,
            className,
            autoScroll = false,
            autoScrollContentRef,
            onScrollEnd,
            scrollEndThreshold = 50,
            scrollRestorationKey,
            onScrollStateChange,
            onAutoScrollChange,
        },
        ref,
    ) => {
        const scrollContainerRef = useRef<HTMLDivElement>(null);
        const prevHeightRef = useRef(0);
        const isAutoScrollEnabledRef = useRef(autoScroll);
        const hasRestoredRef = useRef(false);
        // Tracks whether the user actively scrolled up (wheel with negative
        // deltaY, or touch drag down). Reset when auto-scroll is re-enabled.
        // This is more precise than userInteractedRef for the disable decision
        // because layout shifts and clicks don't set it.
        const userScrolledUpRef = useRef(false);
        const touchStartYRef = useRef<number | null>(null);

        // Helper: update auto-scroll ref and notify consumer in one place.
        const setAutoScroll = useCallback(
            (enabled: boolean) => {
                isAutoScrollEnabledRef.current = enabled;
                if (enabled) {
                    userScrolledUpRef.current = false;
                }
                onAutoScrollChange?.(enabled);
            },
            [onAutoScrollChange],
        );

        // Expose scrollToBottom method via ref
        useImperativeHandle(ref, () => ({
            scrollToBottom: () => {
                if (!scrollContainerRef.current) return;

                // Re-enable auto-scroll so new content keeps following.
                setAutoScroll(true);

                const container = scrollContainerRef.current;
                const distance = container.scrollHeight - container.scrollTop - container.clientHeight;

                // If already at bottom, no need to scroll
                if (distance <= 0) return;

                // Dynamic duration based on distance
                const velocityFactor = 4; // px/ms - tune this for feel
                const duration = Math.max(150, Math.min(600, distance / velocityFactor));

                // Use requestAnimationFrame for custom easing
                const startTime = performance.now();
                const startPos = container.scrollTop;
                const targetPos = container.scrollHeight - container.clientHeight;

                // Ease-out cubic for natural deceleration
                const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

                const animate = (currentTime: number) => {
                    const elapsed = currentTime - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const easedProgress = easeOutCubic(progress);

                    container.scrollTop = startPos + (targetPos - startPos) * easedProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animate);
                    }
                };

                requestAnimationFrame(animate);
            },
        }));

        const debouncedScroll = useThrottledCallback((scrollContainer: HTMLDivElement, scrollTop: number) => {
            scrollContainer.scrollTo({ top: scrollTop, behavior: "smooth" });
        }, 500);

        const debouncedOnScrollEnd = useDebouncedCallback(
            () => {
                onScrollEnd?.();
            },
            500,
            {
                leading: true,
                trailing: false,
            },
        );

        const debouncedSavePosition = useDebouncedCallback(() => {
            if (!scrollContainerRef.current || !scrollRestorationKey) return;
            const { scrollTop, scrollLeft } = scrollContainerRef.current;
            savePosition(scrollRestorationKey, scrollTop, scrollLeft);
        }, 300);

        // Sync the ref when the autoScroll prop changes
        useEffect(() => {
            setAutoScroll(autoScroll);
        }, [autoScroll, setAutoScroll]);

        // Auto-scroll:
        // Instantly scroll to the bottom of the container as soon as possible after the component has
        // mounted and the scroll container is available.
        useEffect(() => {
            if (!autoScroll) {
                return;
            }

            // If a scroll restoration key is provided, we don't want to auto-scroll to the bottom
            // as that would override the restored position.
            if (scrollRestorationKey) {
                return;
            }

            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: "instant",
                });
            }
            // Explicitly not including other dependencies to ensure we only scroll to the bottom once.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [scrollContainerRef.current]);

        // Scroll restoration (non-auto-scroll): restore saved position on mount when
        // autoScroll is off and no autoScrollContentRef is provided (the ResizeObserver
        // path handles the auto-scroll case).
        useMountEffect(() => {
            if (autoScroll || autoScrollContentRef || !scrollRestorationKey || !scrollContainerRef.current) return;
            const storedPosition = getStoredPosition(scrollRestorationKey);
            if (storedPosition && !hasRestoredRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: storedPosition.scrollTop,
                    left: storedPosition.scrollLeft,
                    behavior: "instant",
                });
                hasRestoredRef.current = true;
            }
        });

        // Auto-scroll: Observe height changes of the auto-scroll content and scroll to bottom.
        // The observer stays connected even when auto-scroll is disabled so it can
        // resume immediately when re-enabled (e.g. user scrolls back to bottom)
        // without waiting for a React re-render to reconnect.
        useEffect(() => {
            if (!scrollContainerRef.current || !autoScrollContentRef?.current) return;

            // We're relying on the ResizeObserver to detect height changes of the content, and based on that,
            // trigger auto-scroll to the bottom of the container.
            const resizeObserver = new ResizeObserver(() => {
                const scrollContainer = scrollContainerRef.current;
                const autoScrollContent = autoScrollContentRef.current;

                if (!scrollContainer || !autoScrollContent) {
                    return;
                }

                // Scroll restoration: Restore scroll position if scrollRestorationKey is provided
                if (scrollRestorationKey) {
                    const storedPosition = getStoredPosition(scrollRestorationKey);
                    if (!hasRestoredRef.current && storedPosition) {
                        if (scrollContainer.scrollHeight < storedPosition.scrollTop) {
                            console.log(
                                "ScrollArea: Not restoring scroll position as content is smaller than stored position",
                                {
                                    container: scrollContainer.scrollHeight,
                                    content: autoScrollContent.scrollHeight,
                                    stored: storedPosition.scrollTop,
                                },
                            );
                            return;
                        }

                        // Disable auto-scroll so we maintain the restored position
                        // If the restored position is at the end of the content, auto-scroll will be re-enabled inside of handleScroll
                        // which is invoked by our scrollTo invocation below.
                        setAutoScroll(false);
                        console.log("ScrollArea: Restoring scroll position", storedPosition);
                        scrollContainer.scrollTo({
                            top: storedPosition.scrollTop,
                            left: storedPosition.scrollLeft,
                            behavior: "instant",
                        });
                        hasRestoredRef.current = true;
                        return;
                    }
                }

                // Skip auto-scrolling when disabled, but keep tracking height
                // so we don't trigger a large catch-up scroll when re-enabled.
                const newHeight = autoScrollContent.scrollHeight;
                if (!isAutoScrollEnabledRef.current) {
                    prevHeightRef.current = newHeight;
                    return;
                }

                // Only scroll if height has increased
                const previousHeight = prevHeightRef.current;
                if (newHeight > previousHeight) {
                    debouncedScroll(scrollContainer, newHeight);
                }
                prevHeightRef.current = newHeight;
            });

            resizeObserver.observe(autoScrollContentRef.current);

            // Clean up the observer
            return () => {
                resizeObserver.disconnect();
            };
        }, [debouncedScroll, autoScrollContentRef, debouncedSavePosition, scrollRestorationKey, setAutoScroll]);

        const handleScroll = useCallback(() => {
            if (!scrollContainerRef.current) return;

            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            const bottom = scrollHeight - clientHeight;
            const isAtBottom = scrollTop >= bottom - scrollEndThreshold;

            // Save scroll position if restoration key is provided
            if (scrollRestorationKey) {
                debouncedSavePosition();
            }

            // Check if we're near the end of the scroll area and trigger the callback if provided
            if (onScrollEnd && isAtBottom) {
                debouncedOnScrollEnd();
            }

            // Notify parent component about scroll state changes
            if (onScrollStateChange) {
                onScrollStateChange(isAtBottom);
            }

            // Re-enable auto-scroll when the user scrolls near the bottom.
            if (!isAutoScrollEnabledRef.current && isAtBottom) {
                setAutoScroll(true);
            }
            // Disable auto-scroll only when the user actively scrolled up
            // (wheel/touch gesture, not a layout shift or click).
            else if (userScrolledUpRef.current && isAutoScrollEnabledRef.current) {
                setAutoScroll(false);
                userScrolledUpRef.current = false;
                // Cancel any pending throttled scroll calls.
                debouncedScroll.cancel();
                // Stop the browser's in-progress smooth-scroll animation by
                // anchoring at the current position. Without this the animation
                // keeps firing scroll events that push scrollTop back to the
                // bottom and immediately re-enable auto-scroll.
                scrollContainerRef.current.scrollTo({
                    top: scrollTop,
                    behavior: "instant",
                });
            }
        }, [
            debouncedOnScrollEnd,
            debouncedSavePosition,
            debouncedScroll,
            onScrollEnd,
            scrollEndThreshold,
            scrollRestorationKey,
            onScrollStateChange,
            setAutoScroll,
        ]);

        const handleWheel = useCallback((e: React.WheelEvent) => {
            if (e.deltaY < 0) {
                // Scrolling up
                userScrolledUpRef.current = true;
            } else if (e.deltaY > 0) {
                // Scrolling down — clear the flag so a previous upward
                // gesture doesn't incorrectly disable auto-scroll.
                userScrolledUpRef.current = false;
            }
        }, []);

        const handleTouchStart = useCallback((e: React.TouchEvent) => {
            // Record touch start Y; touchmove will compare to detect direction.
            if (e.touches.length > 0) {
                touchStartYRef.current = e.touches[0].clientY;
            }
        }, []);

        const handleTouchMove = useCallback((e: React.TouchEvent) => {
            if (e.touches.length > 0 && touchStartYRef.current !== null) {
                const currentY = e.touches[0].clientY;
                if (currentY > touchStartYRef.current) {
                    // Touch dragging down = scrolling up
                    userScrolledUpRef.current = true;
                } else if (currentY < touchStartYRef.current) {
                    // Touch dragging up = scrolling down — clear the flag
                    userScrolledUpRef.current = false;
                }
                // Update baseline so direction is tracked continuously
                touchStartYRef.current = currentY;
            }
        }, []);

        const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
            if (e.key === "ArrowUp" || e.key === "PageUp" || e.key === "Home") {
                userScrolledUpRef.current = true;
            } else if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === "End") {
                userScrolledUpRef.current = false;
            }
        }, []);

        return (
            <div
                ref={scrollContainerRef}
                id={id}
                className={cn(
                    // scrollbar-gutter:stable ensures we always reserve space for the scrollbar.
                    // This is important for layout stability, as it means content won't jump around when e.g. resizing the window causes scrollbars to appear/disappear.
                    "[scrollbar-gutter:stable]",
                    {
                        "overflow-x-auto overflow-y-hidden pb-[3px]": orientation === "horizontal",
                        "overflow-y-auto overflow-x-hidden pr-[3px]": orientation === "vertical",
                        "overflow-x-auto overflow-y-auto pb-[3px] pr-[3px]": orientation === "both",
                    },
                    className,
                )}
                onScroll={handleScroll}
                onWheel={handleWheel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onKeyDown={handleKeyDown}
                data-orientation={orientation}
            >
                {children}
            </div>
        );
    },
);

ScrollArea.displayName = "ScrollArea";

// Storage utility functions
function getStoredPosition(scrollRestorationKey: string): { scrollTop: number; scrollLeft: number } | null {
    try {
        const stored = sessionStorage.getItem(scrollRestorationKey);
        if (stored) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const parsed = JSON.parse(stored);
            if (typeof parsed.scrollTop === "number" && typeof parsed.scrollLeft === "number") {
                return {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    scrollTop: parsed.scrollTop,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    scrollLeft: parsed.scrollLeft,
                };
            }
        }
    } catch (e) {
        console.warn("ScrollArea: Failed to parse stored scroll position", e);
    }
    return null;
}

function savePosition(scrollRestorationKey: string, scrollTop: number, scrollLeft: number) {
    console.log("ScrollArea: Saving scroll position", { scrollTop, scrollLeft });
    if (!scrollRestorationKey) return;
    try {
        sessionStorage.setItem(
            scrollRestorationKey,
            JSON.stringify({
                scrollTop,
                scrollLeft,
            }),
        );
    } catch (e) {
        console.warn("ScrollArea: Failed to save scroll position", e);
    }
}
